import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';

import { isAsCsReceiptStatus } from '../../../../../../../shared/utils/asCsStatus';
import { cleanParams } from '../../../../../../../shared/utils/cleanParams';
import { getDateRangeValidationError } from '../../../../../../../shared/utils/time';
import { useModelOptions } from '../../../Common/hooks/useModelOptions';

import type { KeyboardEvent, MouseEvent } from 'react';
import type { IGetAiAgentAsCsListParams } from '../../../../../../../shared/apis/ai-agent/ascs/type';
import type { AiAgentAsCsSearchType, AsCsOrder, AsCsReceiptSortField, IAiAgentAsCsFilterForm } from '../types';

const DEFAULT_PAGE_SIZE = 10;

const createInitialFilterForm = (): IAiAgentAsCsFilterForm => ({
  startDate: null,
  endDate: null,
  receiptStatuses: [],
  modelId: null,
});

const initialSearchType: AiAgentAsCsSearchType = 'serialNumber';

const parseDateParam = (value: string): Date | null => {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateParam = (date: Date | null, isEndOfDay = false): string => {
  if (!date) {
    return '';
  }

  return (isEndOfDay ? dayjs(date).endOf('day') : dayjs(date).startOf('day')).toISOString().substring(0, 19);
};

const isAiAgentAsCsSearchType = (value: string): value is AiAgentAsCsSearchType => {
  return (
    value === 'serialNumber' ||
    value === 'customerName' ||
    value === 'manageNo' ||
    value === 'receiptNo' ||
    value === 'receiptContent'
  );
};

const parsePositiveInteger = (value: string | null, fallback: number): number => {
  const parsedValue = Number(value);
  return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : fallback;
};

const isAsCsSortField = (value: string | null): value is AsCsReceiptSortField => {
  return value === 'manageNo' || value === 'receiptDt';
};

const isAsCsOrder = (value: string | null): value is AsCsOrder => {
  return value === 'ASC' || value === 'DESC';
};

const parseReceiptStatuses = (value: string): IAiAgentAsCsFilterForm['receiptStatuses'] => {
  if (!value) {
    return [];
  }

  return Array.from(
    new Set(
      value
        .split(',')
        .map((status) => status.trim())
        .filter(isAsCsReceiptStatus),
    ),
  );
};

export default function useAiAgentAsCsFilters(companyId: number) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterForm, setFilterForm] = useState<IAiAgentAsCsFilterForm>(createInitialFilterForm);
  const [searchType, setSearchType] = useState<AiAgentAsCsSearchType>(initialSearchType);

  const startDateParam = searchParams.get('receiptStartDt') || searchParams.get('startDate') || '';
  const endDateParam = searchParams.get('receiptEndDt') || searchParams.get('endDate') || '';
  const receiptStatusParam = searchParams.get('receiptStatus') || '';
  const modelIdParam = searchParams.get('modelId') || '';
  const searchTypeParam = searchParams.get('searchType') || initialSearchType;
  const keywordParam = searchParams.get('keyword') || '';
  const keyword = keywordParam.trim();

  const modelOptions = useModelOptions(companyId);
  const parsedStartDate = parseDateParam(startDateParam);
  const parsedEndDate = parseDateParam(endDateParam);
  const appliedSearchType = isAiAgentAsCsSearchType(searchTypeParam) ? searchTypeParam : initialSearchType;
  const parsedModelId = parsePositiveInteger(modelIdParam, 0);
  const receiptStatuses = parseReceiptStatuses(receiptStatusParam);
  const sortByParam = searchParams.get('sortBy');
  const orderParam = searchParams.get('order');
  const order = isAsCsOrder(orderParam) ? orderParam : null;
  const sortBy = order && isAsCsSortField(sortByParam) ? sortByParam : null;

  const queryParams: IGetAiAgentAsCsListParams = {
    companyId,
    page: parsePositiveInteger(searchParams.get('page'), 1),
    size: parsePositiveInteger(searchParams.get('size'), DEFAULT_PAGE_SIZE),
    startDate: parsedStartDate ? formatDateParam(parsedStartDate) : undefined,
    endDate: parsedEndDate ? formatDateParam(parsedEndDate, true) : undefined,
    receiptStatuses: receiptStatuses.join(',') || undefined,
    modelId: parsedModelId || undefined,
    serialNumber: appliedSearchType === 'serialNumber' ? keyword || undefined : undefined,
    customerName: appliedSearchType === 'customerName' ? keyword || undefined : undefined,
    manageNo: appliedSearchType === 'manageNo' ? keyword || undefined : undefined,
    receiptNo: appliedSearchType === 'receiptNo' ? keyword || undefined : undefined,
    receiptContent: appliedSearchType === 'receiptContent' ? keyword || undefined : undefined,
    manageNoSort: sortBy === 'manageNo' && order ? order : undefined,
    receiptDtSort: sortBy === 'receiptDt' && order ? order : undefined,
  };

  const updateFilterForm = (nextFilter: Partial<IAiAgentAsCsFilterForm>) => {
    setFilterForm((prev) => ({
      ...prev,
      ...nextFilter,
    }));
  };

  const handleSearchType = (value: string | null) => {
    setSearchType(value && isAiAgentAsCsSearchType(value) ? value : initialSearchType);
  };

  const resetSearchInput = () => {
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const applyFilters = () => {
    const hasOnlyOneDate = (!!filterForm.startDate && !filterForm.endDate) || (!filterForm.startDate && !!filterForm.endDate);

    if (hasOnlyOneDate) {
      void Swal.fire({
        icon: 'error',
        title: '조회 기간 오류',
        text: '시작일과 종료일을 모두 선택해주세요.',
        confirmButtonText: '확인',
      });

      return;
    }

    const dateRangeValidationError = getDateRangeValidationError(filterForm.startDate, filterForm.endDate);

    if (dateRangeValidationError) {
      void Swal.fire({
        icon: 'error',
        ...dateRangeValidationError,
        confirmButtonText: '확인',
      });

      return;
    }

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: '',
        endDate: '',
        receiptStartDt: formatDateParam(filterForm.startDate),
        receiptEndDt: formatDateParam(filterForm.endDate, true),
        receiptStatus: filterForm.receiptStatuses.join(','),
        modelId: filterForm.modelId || '',
        searchType,
        keyword: inputRef.current?.value.trim() || '',
      }),
    );
  };

  const handleSearch = (event: MouseEvent<HTMLButtonElement> | KeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();

    if ('key' in event && event.key !== 'Enter') {
      return;
    }

    applyFilters();
  };

  const resetFilters = () => {
    const initialFilterForm = createInitialFilterForm();
    resetSearchInput();
    setSearchType(initialSearchType);
    setFilterForm(initialFilterForm);

    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        startDate: '',
        endDate: '',
        receiptStartDt: '',
        receiptEndDt: '',
        receiptStatus: '',
        modelId: '',
        searchType: initialSearchType,
        keyword: '',
        sortBy: '',
        order: '',
      }),
    );
  };

  const handlePageChange = (page: number) => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: String(page),
      }),
    );
  };

  const handleSortChange = (nextSortBy: AsCsReceiptSortField, nextOrder: AsCsOrder | null) => {
    const currentParams = Object.fromEntries(searchParams.entries());

    setSearchParams(
      cleanParams({
        ...currentParams,
        page: '1',
        sortBy: nextOrder ? nextSortBy : 'none',
        order: nextOrder || '',
      }),
    );
  };

  useEffect(() => {
    setFilterForm({
      startDate: parseDateParam(startDateParam),
      endDate: parseDateParam(endDateParam),
      receiptStatuses: parseReceiptStatuses(receiptStatusParam),
      modelId: modelIdParam || null,
    });
    setSearchType(appliedSearchType);

    if (inputRef.current) {
      inputRef.current.value = keywordParam;
    }
  }, [appliedSearchType, endDateParam, keywordParam, modelIdParam, receiptStatusParam, startDateParam]);

  return {
    filterForm,
    queryParams,
    sortBy,
    order,
    searchType,
    inputRef,
    modelOptions,
    handlePageChange,
    handleSortChange,
    updateFilterForm,
    handleSearchType,
    handleSearch,
    resetSearchInput,
    resetFilters,
  };
}
